import { useEffect, useReducer, useRef, useCallback } from 'react';
import { Group } from 'fabric';
import type { Canvas } from 'fabric';
import { usePieceStore } from '../../model/piece-context';
import { isLayerObject, PIECE } from '../../canvas/utils/canvas-pieces';
import { applyGuard } from '../../model/sync/apply-guard';
import type { PieceModel } from '../../model/types';

const MAX_HISTORY = 50;

// A snapshot is just the model — it's immutable, so we keep the reference.
type HistoryState = { snapshots: PieceModel[]; index: number };

type HistoryAction = { type: 'PUSH'; snapshot: PieceModel } | { type: 'UNDO' } | { type: 'REDO' } | { type: 'CLEAR' };

const historyReducer = (state: HistoryState, action: HistoryAction): HistoryState => {
  switch (action.type) {
    case 'PUSH': {
      const kept = state.snapshots.slice(0, state.index + 1);
      const next = [...kept, action.snapshot];
      const trimmed = next.length > MAX_HISTORY ? next.slice(next.length - MAX_HISTORY) : next;
      return { snapshots: trimmed, index: trimmed.length - 1 };
    }
    case 'UNDO':
      return state.index <= 0 ? state : { ...state, index: state.index - 1 };
    case 'REDO':
      return state.index >= state.snapshots.length - 1 ? state : { ...state, index: state.index + 1 };
    case 'CLEAR':
      return { snapshots: [], index: -1 };
  }
};

/**
 * Undo/redo over the model: every model change is a snapshot, and restoring
 * replaces the model — the renderer rebuilds the canvas from there.
 */
export const useHistory = (canvas: Canvas | null) => {
  const [state, dispatch] = useReducer(historyReducer, { snapshots: [], index: -1 });
  const store = usePieceStore();

  // True while we drive the model ourselves (restore, grouped edits), so it doesn't snapshot.
  const suppress = useRef(false);
  const scheduled = useRef(false);

  const takeSnapshot = useCallback(() => {
    if (suppress.current) return;
    dispatch({ type: 'PUSH', snapshot: store.getState() });
  }, [store]);

  // Collapse a burst of model changes in one tick into a single entry.
  const scheduleSnapshot = useCallback(() => {
    if (suppress.current || scheduled.current) return;
    scheduled.current = true;
    queueMicrotask(() => {
      scheduled.current = false;
      takeSnapshot();
    });
  }, [takeSnapshot]);

  // Run a multi-step (possibly async) operation as one history entry.
  const transaction = useCallback(
    async (fn: () => void | Promise<void>) => {
      if (suppress.current) {
        await fn();
        return;
      }
      suppress.current = true;
      try {
        await fn();
      } finally {
        suppress.current = false;
        scheduleSnapshot();
      }
    },
    [scheduleSnapshot]
  );

  const restore = useCallback(
    (model: PieceModel) => {
      if (!canvas) return;
      suppress.current = true;
      // The reconciler (applyModelToFabric) reuses/updates/removes layer objects in place,
      // so undoing a move no longer reloads images/videos (no flicker). Only tear down what
      // the reconciler can't patch in place: groups (rebuilt from scratch) and the derived
      // effect overlay (re-derived by the effect reconcilers).
      applyGuard.begin();
      canvas
        .getObjects()
        .filter(o => o.pieceId === PIECE.ALPHA_FALLOFF || (isLayerObject(o) && o instanceof Group))
        .forEach(o => canvas.remove(o));
      applyGuard.end();
      store.dispatch({ type: 'replaceModel', model });
      suppress.current = false;
    },
    [canvas, store]
  );

  const clearHistory = useCallback(() => dispatch({ type: 'CLEAR' }), []);

  const undo = useCallback(() => {
    if (state.index <= 0) return;
    restore(state.snapshots[state.index - 1]);
    dispatch({ type: 'UNDO' });
  }, [state, restore]);

  const redo = useCallback(() => {
    if (state.index >= state.snapshots.length - 1) return;
    restore(state.snapshots[state.index + 1]);
    dispatch({ type: 'REDO' });
  }, [state, restore]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.key === 'Z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // The model is the single source: snapshot whenever it changes.
  useEffect(() => store.subscribe(scheduleSnapshot), [store, scheduleSnapshot]);

  return {
    clearHistory,
    undo,
    redo,
    canUndo: state.index > 0,
    canRedo: state.index < state.snapshots.length - 1,
    transaction
  };
};
